import { StandardTextFieldProps } from '@material-ui/core/TextField/TextField';
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { batch, connect, useSelector } from 'react-redux';
import { EmeraldErrorModal } from 'src/common/api/emerald-error';
import ProductCodeContainer from 'src/common/components/code-container/components/product-code-container';
import {
  restorePage,
  setHomePage,
} from 'src/common/components/emerald-home-page/emerald-home-page';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { getValueAsNumber } from 'src/common/helpers/number-helper';
import { getValueAsString } from 'src/common/helpers/string-helper';
import { ITenderExchange } from 'src/common/interfaces/dtos/ITenderExchange';
import { AppState, store } from 'src/common/redux/configure-store';
import { TenderExchangeActions } from 'src/common/redux/reducers/tender-exchange-reducer';
import { resetCardData } from 'src/devices/payment/get-card-details';
import Keypad from 'src/keypad/components/keypad';
import { priceFormat } from 'src/keypad/keypad-operation';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { tenderExchangeSwipeAhead } from '../business-logic/handle-tender-exchange';
import TenderExchangeWindow from './tender-exchange-window';

const defaultInputValues: StandardTextFieldProps = {
  name: '0.00',
  error: false,
  helperText: '',
};

type Props = typeof mapDispatchToProps &
  ReturnType<typeof mapStateToProps> &
  ITenderExchangeViewProps;

export const showTenderExchangePage = (props: ITenderExchangeViewProps) => {
  store.dispatch(TenderExchangeActions.setTenderExchangeState(true));
  setHomePage(<TenderExchangePage {...props} />, props.onVoucherSubmit);
};

export const closeTenderExchangePage = (): void => {
  restorePage();
  batch((): void => {
    store.dispatch(TenderExchangeActions.setTenderExchangeState(false));
    store.dispatch(TenderExchangeActions.resetTenderExchangeDetails());
  });
};

export const TenderExchangeContainer: React.FC<Props> = (props: Props): JSX.Element => {
  const {
    tenderExchange,
    tenderExchangeDetails: {
      paymentTender,
      refundTender,
      isRefundScreenVisible,
      isSingleTenderExchange,
    },
    updateTenderExchangeDetails,
    onSubmit,
    onCancel,
    onVoucherSubmit,
  } = props;
  const { t } = useTranslation();

  const isTenderExchange = useSelector(
    (state: AppState): boolean => state.tenderExchange.isTenderExchange,
  );
  const isTenderClicked = useSelector(
    (state: AppState): boolean | undefined =>
      state.tenderExchange.tenderExchangeDetails.isTenderClicked,
  );
  const [amountInputElement, setAmountInputElement] = useState<StandardTextFieldProps>(
    defaultInputValues,
  );
  const [displayInputField, setDisplayInputField] = useState<boolean>(true);

  const inputRef = useRef<HTMLInputElement>(null);
  const hasPinpad = useSelector((state: AppState): boolean => state.pinPad.isPinpadConfigured);
  const sanitizedValidationFieldText = (text: string) => priceFormat(text, inputRef);
  const onTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { value } = event.target;
    setAmountInputElement({
      name: getValueAsString(sanitizedValidationFieldText(value)),
      error: false,
      helperText: '',
    });
  };

  const handleSwipeAhead = useCallback(async () => {
    const epsData = await tenderExchangeSwipeAhead();

    if (epsData) {
      const tenderGroup = isRefundScreenVisible
        ? tenderExchange.refundTenders
        : tenderExchange.purchaseTenders;
      const tenderSelected = tenderGroup.find(tender => {
        return tender.tenderReferences?.includes(epsData?.card.cardCode || '');
      });
      if (tenderSelected) {
        if (isRefundScreenVisible) {
          updateTenderExchangeDetails({ refundTender: tenderSelected });
          onSubmit().catch(popupMessage);
        } else {
          updateTenderExchangeDetails({ paymentTender: tenderSelected });
        }
      } else {
        resetCardData();
        showSwipAheadInvalidTenderError(handleSwipeAhead);
      }
    }
  }, [
    tenderExchange.purchaseTenders,
    updateTenderExchangeDetails,
    isRefundScreenVisible,
    tenderExchange.refundTenders,
    onSubmit,
  ]);

  useEffect(() => {
    if (isTenderExchange && !isSingleTenderExchange && hasPinpad && !isTenderClicked) {
      handleSwipeAhead();
    }
  }, [handleSwipeAhead, isTenderExchange, isSingleTenderExchange, hasPinpad, isTenderClicked]);

  const onKeypadSubmit = async (value: string): Promise<void> => {
    if (!paymentTender && !refundTender) {
      return onVoucherSubmit(value).catch(popupMessage);
    } else {
      const amount = getValueAsNumber(amountInputElement.name);
      return onSubmit(amount)
        .then(() => setAmountInputElement(defaultInputValues))
        .catch(popupMessage);
    }
  };

  const onBack = (): void => {
    updateTenderExchangeDetails({
      paymentTender: undefined,
      isRefundScreenVisible: false,
      isTenderClicked: false,
    });
  };

  return (
    <ProductCodeContainer
      headerTitle={t('TenderExchangeCommand')}
      onKeypadSubmit={() => {}}
      keypadComponent={
        <Keypad
          keyboardId='tender-exchange'
          visible={!isRefundScreenVisible}
          submitButton={{
            label: 'OK',
            onClick: onKeypadSubmit,
          }}
          disableQuantity={true}
          disableManualWeight={true}
          inputInstructionsLabelText={'Typically up to 13 digits'}
          keypadTestIdPrefix='tender-exchange-'
          noInputField={!displayInputField}
          replaceDecimalInput={!displayInputField}
        />
      }
    >
      <TenderExchangeWindow
        onCancel={onCancel}
        tenderExchange={tenderExchange}
        updateTenderExchangeDetails={updateTenderExchangeDetails}
        paymentTender={paymentTender}
        refundTender={refundTender}
        showRefundScreens={isRefundScreenVisible}
        onBack={onBack}
        onTextChange={onTextChange}
        amountInputElement={amountInputElement}
        setDisplayInputField={setDisplayInputField}
        onSubmit={onSubmit}
        onKeypadSubmit={onKeypadSubmit}
        onVoucherPayment={onVoucherSubmit}
        inputRef={inputRef}
      />
    </ProductCodeContainer>
  );
};

export interface ITenderExchangeViewProps {
  onCancel: () => void;
  onSubmit: (amount?: number) => Promise<void>;
  onVoucherSubmit: (barcode: string) => Promise<void>;
  tenderExchange: ITenderExchange;
}

const mapStateToProps = (state: AppState) => ({
  tenderExchangeDetails: state.tenderExchange.tenderExchangeDetails,
});

const mapDispatchToProps = {
  updateTenderExchangeDetails: TenderExchangeActions.updateTenderExchangeDetails,
};

const TenderExchangePage = connect(mapStateToProps, mapDispatchToProps)(TenderExchangeContainer);
export default TenderExchangePage;

const showSwipAheadInvalidTenderError = (onAccept: () => Promise<void>) => {
  popupMessage(
    new EmeraldErrorModal({
      type: EmeraldDialogTypes.Error,
      message: 'InvalidTender',
      subMessage: 'InvalidTender_SubHeader',
      onAccept,
      acceptButtonLabel: 'Close',
    }),
  );
};
